import {
  useDeleteMarketplaceDashboardImage,
  useMarketplaceDashboard,
  useUpdateMarketplaceDashboardMetadata,
  useUpdateMarketplaceDashboardPublication,
  useUploadMarketplaceDashboardImage,
} from "@/api/hooks/marketplace";
import { updateProfile } from "@/api/hooks/profile";
import { useMarketplaceImage } from "@/marketplace/useMarketplaceImage";
import { getMarketplaceAssetId } from "@/marketplace/utils";
import { FileUploader } from "@/overlay/components/CommonParts/FileUploader";
import { Modal } from "@/overlay/components/window/Modal";
import { useAppDispatch } from "@/reducer";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Input, SelectField, SelectionField, Toggle } from "@mm/metx-design-system";
import { type MarketplaceProfileMetadata, MarketplaceTier, type SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import "./edit-modal.scss";

const TIER_OPTIONS = [
  { label: "Core", value: MarketplaceTier.Core },
  { label: "Pro", value: MarketplaceTier.Pro },
  { label: "Premium", value: MarketplaceTier.Premium },
];

type EditModalProps = {
  profile: SlimProfile;
  isOpen: boolean;
  onClose: () => void;
};

type FormValues = {
  title: string;
  description: string;
  tier: MarketplaceTier;
  published: boolean;
};

export function EditModal({ profile, isOpen, onClose }: EditModalProps) {
  const dispatch = useAppDispatch();
  const { data: dashboard, isLoading } = useMarketplaceDashboard(profile.id, {
    enabled: isOpen,
    retry: false,
  });
  const updateMetadata = useUpdateMarketplaceDashboardMetadata();
  const updatePublication = useUpdateMarketplaceDashboardPublication();
  const uploadImage = useUploadMarketplaceDashboardImage();
  const deleteImage = useDeleteMarketplaceDashboardImage();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { dirtyFields, errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      title: profile.title,
      description: "",
      tier: MarketplaceTier.Core,
      published: false,
    },
  });

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [existingFileId, setExistingFileId] = useState<number>();
  const [newImage, setNewImage] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    clearErrors("root.serverError");
    setNewImage(null);
    setImageRemoved(false);
    if (dashboard) {
      const nextTitle = dashboard.title || profile.title;
      const nextDescription = dashboard.description ?? "";
      const nextTier = dashboard.tier ?? MarketplaceTier.Core;
      const nextPublished = dashboard.published ?? false;
      const nextImageUrl = dashboard.image_url ?? null;
      reset({
        title: nextTitle,
        description: nextDescription,
        tier: nextTier,
        published: nextPublished,
      });
      setImageUrl(nextImageUrl);
      setExistingFileId(nextImageUrl ? getMarketplaceAssetId(nextImageUrl) : undefined);
      return;
    }
    if (!isLoading) {
      reset({
        title: profile.title,
        description: "",
        tier: MarketplaceTier.Core,
        published: false,
      });
      setImageUrl(null);
      setExistingFileId(undefined);
    }
  }, [clearErrors, dashboard, isLoading, isOpen, profile.title, reset]);

  const remotePreview = useMarketplaceImage(imageRemoved ? null : imageUrl);
  const localPreview = useMemo(() => (newImage ? URL.createObjectURL(newImage) : undefined), [newImage]);
  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  const previewSrc = localPreview ?? remotePreview;

  const handleSave = async ({ title, description, tier, published }: FormValues) => {
    clearErrors("root.serverError");
    try {
      const nextTitle = title.trim();
      if (nextTitle !== profile.title) {
        await dispatch(
          updateProfile({
            profileId: profile.id,
            data: { id: profile.id, title: nextTitle },
          }),
        ).unwrap();
      }
      if (newImage) {
        await uploadImage.mutateAsync({
          profileId: profile.id,
          file: newImage,
        });
      } else if (imageRemoved && existingFileId) {
        try {
          await deleteImage.mutateAsync(existingFileId);
        } catch {
          // File delete is account-scoped; metadata unlink still persists
        }
      }
      const metadata: MarketplaceProfileMetadata = {
        description: description.trim() || null,
        tier,
      };
      if (imageRemoved && !newImage) {
        metadata.image_file_id = null;
      }
      await updateMetadata.mutateAsync({ profileId: profile.id, metadata });
      if (dirtyFields.published) {
        await updatePublication.mutateAsync({
          profileId: profile.id,
          publication: { published },
        });
      }
      onClose();
    } catch {
      setError("root.serverError", {
        type: "server",
        message: t`Couldn't save marketplace dashboard. Please try again.`,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!isSubmitting}>
      <div className="edit-marketplace-modal">
        <h2 className="edit-marketplace-modal__title">
          <Trans>Dashboard marketplace</Trans>
        </h2>
        {isLoading ? (
          <mm-loader size="large" />
        ) : (
          <form onSubmit={handleSubmit(handleSave)}>
            <div className="edit-marketplace-modal__body">
              <Controller
                control={control}
                name="title"
                rules={{
                  validate: (value) => value.trim().length > 0 || t`Please add a dashboard title.`,
                }}
                render={({ field }) => (
                  <Input
                    label={t`Title`}
                    fullWidth
                    value={field.value}
                    onChange={(event) => field.onChange(event.currentTarget.value)}
                  />
                )}
              />
              {errors.title?.message && <p className="edit-marketplace-modal__error">{errors.title.message}</p>}
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <Input
                    type="textarea"
                    label={t`Description`}
                    fullWidth
                    rows={4}
                    value={field.value}
                    onChange={(event) => field.onChange(event.currentTarget.value)}
                    placeholder={t`Provide a description of the dashboard, what purpose does it serve or for whom it is intended.`}
                  />
                )}
              />
              <div className="edit-marketplace-modal__cover">
                {previewSrc ? (
                  <img className="edit-marketplace-modal__cover-preview" src={previewSrc} alt="" />
                ) : (
                  <FileUploader
                    header={t`Add cover image`}
                    allowMultiple={false}
                    extraHeight
                    accept="image/png,image/jpeg,image/webp"
                    onSubmit={(files) => {
                      const file = files.item(0);
                      if (file) {
                        setNewImage(file);
                        setImageRemoved(false);
                      }
                    }}
                  />
                )}
                {previewSrc && (
                  <Button
                    className="edit-marketplace-modal__remove"
                    variant="danger"
                    leftIcon={<span className="material-symbols-outlined">delete_outline</span>}
                    onClick={() => {
                      setNewImage(null);
                      setImageRemoved(true);
                      setImageUrl(null);
                    }}
                  >
                    <Trans>Remove image</Trans>
                  </Button>
                )}
              </div>
              <Controller
                control={control}
                name="tier"
                render={({ field }) => (
                  <SelectField
                    label={t`Select plan`}
                    fullWidth
                    required
                    options={TIER_OPTIONS}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value as MarketplaceTier);
                    }}
                  />
                )}
              />
              {errors.root?.serverError?.message && (
                <p className="edit-marketplace-modal__error">{errors.root.serverError.message}</p>
              )}
            </div>
            <div className="edit-marketplace-modal__footer">
              <Controller
                control={control}
                name="published"
                render={({ field }) => (
                  <SelectionField
                    label={t`Publish`}
                    control={
                      <Toggle checked={field.value} onChange={(event) => field.onChange(event.currentTarget.checked)} />
                    }
                  />
                )}
              />
              <div className="edit-marketplace-modal__footer-actions">
                <Button variant="secondary" disabled={isSubmitting} onClick={onClose}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button variant="primary" type="submit" loading={isSubmitting}>
                  <Trans>Save</Trans>
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
